import { useState, useEffect } from "react";

const DetailPageReviewImages = ({ images, videos }: { images: string[], videos: string[] }) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  // Combine all media (images and videos) into a single array
  const allMedia = [
    ...images.filter(img => img !== "string").map(img => ({ type: 'image', src: img })),
    ...videos.filter(vid => vid !== "string").map(vid => ({ type: 'video', src: vid }))
  ];

  const handleMediaClick = (index: number) => {
    setCurrentIndex(index);
    setIsModalOpen(true);
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? allMedia.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === allMedia.length - 1 ? 0 : prev + 1));
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  // Keyboard navigation and body scroll lock
  useEffect(() => {
    if (!isModalOpen) return;

    // Lock body scroll
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      } else if (e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      // Unlock body scroll
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen, allMedia.length]);

  return (
    <>
      <div className=" d-flex justify-content-start align-items-center gap-2 mt-3 mb-3">
        {allMedia.map((media, index) => (
          <div key={index} onClick={() => handleMediaClick(index)} className="cursor-pointer">
            {media.type === 'image' ? (
              <div className="mini-pro-img mb-1">
                <img
                  src={media.src}
                  alt={`media-${index}`}
                  className="mini-product-img p-1"
                  loading="lazy"
                />
              </div>
            ) : (
              <div className="mini-pro-img mb-1 position-relative">
                <video src={media.src} className="mini-product-img p-1" />
                <div className="position-absolute top-50 start-50 translate-middle">
                  <i className="bi bi-play-circle-fill text-white fs-3"></i>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Media Viewer Modal */}
      {isModalOpen && (
        <div 
          className="modal fade show d-block" 
          style={{ backgroundColor: 'rgba(0,0,0,0.9)' }}
          onClick={handleClose}
        >
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content bg-transparent border-0" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header border-0 position-absolute top-0 end-0 z-index-3">
                <button
                  type="button"
                  className="btn btn-link text-white fs-3 p-2"
                  onClick={handleClose}
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
              
              <div className="modal-body p-0 position-relative">
                {/* Navigation Buttons */}
                {allMedia.length > 1 && (
                  <>
                    <button
                      className="btn btn-link text-white position-absolute top-50 start-0 translate-middle-y z-index-2 fs-1"
                      onClick={handlePrevious}
                      style={{ left: '20px' }}
                    >
                      <i className="bi bi-chevron-left"></i>
                    </button>
                    <button
                      className="btn btn-link text-white position-absolute top-50 end-0 translate-middle-y z-index-2 fs-1"
                      onClick={handleNext}
                      style={{ right: '20px' }}
                    >
                      <i className="bi bi-chevron-right"></i>
                    </button>
                  </>
                )}

                {/* Media Display */}
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '70vh' }}>
                  {allMedia[currentIndex]?.type === 'image' ? (
                    <img
                      src={allMedia[currentIndex].src}
                      alt={`media-${currentIndex}`}
                      className="img-fluid"
                      style={{ maxHeight: '85vh', maxWidth: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    <video
                      src={allMedia[currentIndex]?.src}
                      controls
                      autoPlay
                      className="img-fluid"
                      style={{ maxHeight: '85vh', maxWidth: '100%', objectFit: 'contain' }}
                    />
                  )}
                </div>

                {/* Counter */}
                <div className="position-absolute bottom-0 start-50 translate-middle-x mb-3">
                  <span className="badge bg-dark text-white px-3 py-2">
                    {currentIndex + 1} / {allMedia.length}
                  </span>
                </div>

                {/* Thumbnails */}
                {allMedia.length > 1 && (
                  <div className="d-flex justify-content-center gap-2 mt-3 pb-3 overflow-auto">
                    {allMedia.map((media, index) => (
                      <div
                        key={index}
                        onClick={() => setCurrentIndex(index)}
                        className={`cursor-pointer border ${index === currentIndex ? 'border-3 border-white' : 'border-1 border-secondary'}`}
                        style={{ width: '60px', height: '60px', opacity: index === currentIndex ? 1 : 0.6 }}
                      >
                        {media.type === 'image' ? (
                          <img
                            src={media.src}
                            alt={`thumb-${index}`}
                            className="w-100 h-100"
                            style={{ objectFit: 'cover' }}
                          />
                        ) : (
                          <div className="position-relative w-100 h-100">
                            <video src={media.src} className="w-100 h-100" style={{ objectFit: 'cover' }} />
                            <div className="position-absolute top-50 start-50 translate-middle">
                              <i className="bi bi-play-circle-fill text-white fs-6"></i>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DetailPageReviewImages;
